import { Add as AddIcon } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { type DateTime } from "luxon";
import { useCallback, useState } from "react";

import { Dialog } from "../../atoms/Dialog/Dialog";
import {
  Date,
  FIELD_NAME_DATE,
  Form,
  type FormProps,
  ResetButton,
  SubmitButton,
  type TextFieldValue,
} from "../../atoms/Form";
import {
  AssignTo,
  Description,
  FIELD_NAME_ASSIGN_TO,
  FIELD_NAME_DESCRIPTION,
  FIELD_NAME_NAME,
  Name,
} from "../../molecules/Form";

// Centralized strings for internationalization
const tAddNewTask = "Add New Task";
const tAddTask = "Add Task";
const tCancel = "Cancel";
const tCreate = "Create Task";
const tDueDate = "Due Date";

const requiredFields = [FIELD_NAME_NAME, FIELD_NAME_DATE];

interface FormValues {
  [FIELD_NAME_NAME]: TextFieldValue;
  [FIELD_NAME_DESCRIPTION]: TextFieldValue;
  [FIELD_NAME_DATE]: DateTime | null;
  [FIELD_NAME_ASSIGN_TO]: string;
}

interface NewTask {
  name: string;
  description: string;
  dueDate: Date;
  userId?: string;
}

interface Member {
  id: string;
  name: string;
}

interface TaskFormDialogProps {
  onAddTask: (task: NewTask) => void;
  members?: Member[];
}

export const TaskFormDialog = ({ onAddTask, members = [] }: TaskFormDialogProps) => {
  const [open, setOpen] = useState(false);

  const closeDialog = useCallback(() => setOpen(false), []);
  const openDialog = useCallback(() => setOpen(true), []);

  const handleSubmit = useCallback<FormProps<FormValues>["onSubmit"]>(
    (formValues: FormValues, form) => {
      const name = formValues[FIELD_NAME_NAME];
      const dueDate = formValues[FIELD_NAME_DATE];
      const userId = formValues[FIELD_NAME_ASSIGN_TO];

      if (name && dueDate) {
        const task: NewTask = {
          description: formValues[FIELD_NAME_DESCRIPTION] || "",
          dueDate: dueDate.toJSDate(),
          name,
          userId: userId && userId !== "" ? userId : undefined,
        };

        onAddTask(task);
        form?.reset();
        closeDialog();
      }
    },
    [onAddTask, closeDialog],
  );

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={openDialog}
        sx={{
          minWidth: "auto",
          textTransform: "none",
        }}
      >
        {tAddTask}
      </Button>

      <Form<FormValues> onSubmit={handleSubmit}>
        <Dialog
          actions={
            <Box display="grid" gap="2rem" gridAutoColumns="minmax(0, 1fr)" gridAutoFlow="row" padding="1rem">
              <Date
                label={tDueDate}
                datePickerProps={{
                  disablePast: true,
                }}
                required
              />
              <Name required />
              <Description />
              <AssignTo members={members} />
              <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <ResetButton onClick={closeDialog}>{tCancel}</ResetButton>
                <SubmitButton onClick={closeDialog} requiredFields={requiredFields}>
                  {tCreate}
                </SubmitButton>
              </Box>
            </Box>
          }
          dialogType="custom"
          maxWidth="sm"
          onDialogClose={closeDialog}
          open={open}
          title={tAddNewTask}
        />
      </Form>
    </>
  );
};
